import { useState } from 'react';
import {
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Money } from './Money';
import { ResponsiveModal } from './ResponsiveModal';
import { webPrivacyProps } from '../lib/privacy';
import { formatCurrency } from '../lib/utils';

export interface PerDiemResult {
  perDiem: number;
  rawPerDiem: number;
  previousPerDiem: number;
  rawPreviousPerDiem: number;
  hasPreviousDayComparison: boolean;
  isFirstDayOfMonth: boolean;
  daysRemaining: number;
  monthlyIncome: number;
  monthlyFixedExpenses: number;
  monthlyDiscretionarySpend: number;
  dailyDiscretionaryBudget: number;
  isOverBudget: boolean;
  overBudgetAmount: number;
  monthlyDiscretionaryBudget: number;
  reservedRecurring?: number;
}

export interface FireImpact {
  dailyDiff: number;
  monthsDiff: number;
  isUnderspending: boolean;
}

interface PerDiemCardProps {
  perDiem: PerDiemResult | null | undefined;
  fireImpact?: FireImpact | null;
  className?: string;
  title?: string;
  showExplanationLink?: boolean;
}

const FOREST_GRADIENT = ['#173D2A', '#1F5237'] as const;
const OVERBUDGET_GRADIENT = ['#7F1D1D', '#991B1B'] as const;

export function PerDiemCard({
  perDiem,
  fireImpact,
  className = '',
  title = 'Your Per Diem',
  showExplanationLink = false,
}: PerDiemCardProps) {
  const isOverBudget = perDiem?.isOverBudget ?? false;
  const trend = perDiem ? perDiem.perDiem - perDiem.previousPerDiem : 0;
  const showTrend =
    perDiem?.hasPreviousDayComparison &&
    !perDiem.isFirstDayOfMonth &&
    Math.abs(trend) >= 0.01;
  const [showExplanation, setShowExplanation] = useState(false);

  const reservedRecurring = perDiem?.reservedRecurring ?? 0;
  const budgetedDiscretionary = perDiem
    ? perDiem.monthlyDiscretionaryBudget + reservedRecurring
    : 0;
  const discretionaryRemaining = perDiem
    ? budgetedDiscretionary - reservedRecurring - perDiem.monthlyDiscretionarySpend
    : 0;

  const gradient = isOverBudget ? OVERBUDGET_GRADIENT : FOREST_GRADIENT;
  const labelColor = 'rgba(255,255,255,0.5)';
  const subtleColor = 'rgba(255,255,255,0.55)';
  const positiveTrendColor = 'rgba(255,255,255,0.6)';
  const negativeTrendColor = 'rgba(255,160,140,0.9)';

  return (
    <>
      <View className={`rounded-[18px] overflow-hidden shadow-card ${className}`}>
        <LinearGradient
          colors={gradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ padding: 28, flex: 1 }}
        >
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: 100,
              backgroundColor: 'rgba(255,255,255,0.03)',
            }}
          />
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              bottom: -60,
              left: -20,
              width: 160,
              height: 160,
              borderRadius: 80,
              backgroundColor: 'rgba(255,255,255,0.025)',
            }}
          />

          <Text
            className="text-[10.5px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: labelColor }}
          >
            {title}
          </Text>

          {perDiem ? (
            <>
              <View className="flex-row items-end gap-3 mt-3">
                <Money
                  weight={500}
                  className="text-[52px] text-white leading-[1.1]"
                  style={{ letterSpacing: -1.5 }}
                >
                  {formatCurrency(perDiem.perDiem)}
                </Money>
                {showTrend && (
                  <Money
                    className="text-base mb-1.5"
                    style={{
                      color: trend >= 0 ? positiveTrendColor : negativeTrendColor,
                    }}
                  >
                    {trend >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(trend))}
                  </Money>
                )}
              </View>
              <Text
                className="text-[13px] mt-2"
                style={{ color: subtleColor }}
                {...webPrivacyProps('financial')}
              >
                {isOverBudget
                  ? `Over budget by ${formatCurrency(perDiem.overBudgetAmount)} this month`
                  : perDiem.isFirstDayOfMonth
                    ? 'New month, clean slate!'
                    : `Resets to ${formatCurrency(perDiem.dailyDiscretionaryBudget)} in ${perDiem.daysRemaining} day${perDiem.daysRemaining !== 1 ? 's' : ''}`}
              </Text>
              {isOverBudget && (
                <Text className="text-[13px] mt-1" style={{ color: subtleColor }}>
                  {perDiem.daysRemaining} day{perDiem.daysRemaining !== 1 ? 's' : ''} left to hold spending flat
                </Text>
              )}
              {showExplanationLink && (
                <Pressable onPress={() => setShowExplanation(true)} hitSlop={8} className="mt-3 self-start">
                  <Text
                    className="text-xs font-semibold underline"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    How is this calculated?
                  </Text>
                </Pressable>
              )}
              {fireImpact && (
                <Text
                  className="text-xs mt-2"
                  style={{
                    color: fireImpact.isUnderspending ? positiveTrendColor : negativeTrendColor,
                  }}
                  {...webPrivacyProps('financial')}
                >
                  {fireImpact.isUnderspending
                    ? `Saving ${formatCurrency(fireImpact.dailyDiff, { decimals: 0 })}/day extra → FI ${fireImpact.monthsDiff} mo sooner`
                    : `Overspending by ${formatCurrency(fireImpact.dailyDiff, { decimals: 0 })}/day → FI ${Math.abs(fireImpact.monthsDiff)} mo later`}
                </Text>
              )}
            </>
          ) : (
            <Text className="text-2xl font-bold text-white mt-3">&mdash;</Text>
          )}
        </LinearGradient>
      </View>

      {perDiem && showExplanationLink && (
        <ResponsiveModal
          visible={showExplanation}
          onClose={() => setShowExplanation(false)}
          header={(
            <Text className="text-xl font-bold text-gray-900 dark:text-white">
              How Per Diem Is Calculated
            </Text>
          )}
          subtitle="Your daily discretionary amount based on this month's budget and spending so far"
          maxHeight={760}
          androidMinHeight={360}
        >
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
          >
            <View className="pb-5 gap-4">
              <Text className="text-sm leading-6 text-gray-600 dark:text-gray-300">
                Per diem is the amount you can safely spend per day on discretionary purchases
                and still stay on budget for the rest of the month.
              </Text>

              <View className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40">
                <Text className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                  Formula
                </Text>
                <View className="mt-4 items-center">
                  <Text
                    className="text-center text-base font-semibold leading-7 text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    {formatCurrency(budgetedDiscretionary, { decimals: 0 })} discretionary budget
                    {' '}− {formatCurrency(reservedRecurring, { decimals: 0 })} recurring expenses
                    {' '}− {formatCurrency(perDiem.monthlyDiscretionarySpend, { decimals: 0 })} spent so far
                  </Text>
                  <View className="mt-3 h-px w-full bg-gray-200 dark:bg-gray-700" />
                  <Text className="mt-3 text-center text-base font-semibold text-gray-900 dark:text-gray-100">
                    {perDiem.daysRemaining} day{perDiem.daysRemaining !== 1 ? 's' : ''} remaining
                  </Text>
                  <Text
                    className="mt-4 text-center text-lg font-semibold text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    = {formatCurrency(perDiem.perDiem)} per day
                  </Text>
                </View>
              </View>

              <View className="gap-3">
                <View className="flex-row items-start justify-between gap-4">
                  <Text className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    1. Budgeted discretionary spending
                  </Text>
                  <Text
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    {formatCurrency(budgetedDiscretionary, { decimals: 0 })}
                  </Text>
                </View>
                <Text className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                  This is the part of your monthly budget meant for flexible spending. Mandatory categories are already excluded before we get here.
                </Text>

                <View className="h-px bg-gray-100 dark:bg-gray-700" />

                <View className="flex-row items-start justify-between gap-4">
                  <Text className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    2. Subtract recurring expenses
                  </Text>
                  <Text
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    -{formatCurrency(reservedRecurring, { decimals: 0 })}
                  </Text>
                </View>
                <Text className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                  These are predictable recurring charges you chose to smooth out of your daily per diem.
                </Text>

                <View className="h-px bg-gray-100 dark:bg-gray-700" />

                <View className="flex-row items-start justify-between gap-4">
                  <Text className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    3. Subtract discretionary spending already posted this month
                  </Text>
                  <Text
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    -{formatCurrency(perDiem.monthlyDiscretionarySpend, { decimals: 0 })}
                  </Text>
                </View>
                <Text className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                  We only count discretionary transactions that have already hit this month.
                </Text>

                <View className="h-px bg-gray-100 dark:bg-gray-700" />

                <View className="flex-row items-start justify-between gap-4">
                  <Text className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    4. Discretionary amount left for the rest of the month
                  </Text>
                  <Text
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                    {...webPrivacyProps('financial')}
                  >
                    {formatCurrency(discretionaryRemaining, { decimals: 0 })}
                  </Text>
                </View>

                <View className="h-px bg-gray-100 dark:bg-gray-700" />

                <View className="flex-row items-start justify-between gap-4">
                  <Text className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    5. Divide by days remaining
                  </Text>
                  <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {perDiem.daysRemaining}
                  </Text>
                </View>

                <View className="rounded-2xl bg-green-50 px-4 py-4 dark:bg-green-900/20">
                  <Text className="text-xs font-medium uppercase tracking-[0.2em] text-green-700 dark:text-green-400">
                    Result
                  </Text>
                  <Text
                    className="mt-2 text-2xl font-bold text-green-700 dark:text-green-400"
                    {...webPrivacyProps('financial')}
                  >
                    {formatCurrency(perDiem.perDiem)} / day
                  </Text>
                  <Text className="mt-2 text-sm leading-6 text-green-800 dark:text-green-300">
                    This is what you can safely spend per day on anything you&apos;d like and still stay on budget.
                  </Text>
                </View>
              </View>
            </View>
          </ScrollView>
        </ResponsiveModal>
      )}
    </>
  );
}
